import { isDesktop } from '@/lib/platform';
import { commands, type OAuthTokens } from '@/lib/platform/generated/bindings.gen';

import { generateCodeChallenge, generateCodeVerifier, generateState } from './pkce';

/**
 * Auth0 OAuth 2.0 / PKCE client.
 *
 * Talks directly to Auth0's `/authorize`, `/oauth/token`, and `/oauth/revoke`
 * endpoints — no Auth0 SDK. Endpoints live on our custom Auth0 domain
 * (`auth.dev.nula.so` / `auth.staging.nula.so` / `auth.nula.so`) so Auth0
 * never appears in browser URLs.
 *
 * The Auth0 application must be configured as a Single Page Application with:
 *   - PKCE (no client_secret)
 *   - Refresh Token Rotation
 *   - These redirect URIs registered:
 *       http://localhost:5173/auth/callback   (Tauri dev)
 *       http://localhost:1530/auth/callback   (web dev)
 *       http://localhost:1531/auth/callback   (web dev #2)
 *       https://app.stage.nula.so/auth/callback              (web)
 *       https://app.nula.so/auth/callback                    (web)
 *       https://app.staging.nulanotes.com/auth/desktop       (Tauri bounce → nula://)
 *       https://app.nulanotes.com/auth/desktop               (Tauri bounce → nula://)
 *       https://app.staging.nula.so/auth/desktop             (also allowed)
 *       https://app.nula.so/auth/desktop                     (also allowed)
 *
 *   Web uses its own origin (app.nula.so). The desktop bounce lands on the
 *   web_domain origin the build is pointed at via VITE_DESKTOP_WEB_ORIGIN —
 *   app.nulanotes.com / app.staging.nulanotes.com (Terraform web_domain). The
 *   nula.so /auth/desktop URLs are also registered for forward-compat in case
 *   a future build targets that origin.
 *
 * Tauri prod redirects to the hosted `/auth/desktop` page rather than
 * `nula://callback` directly: the page renders a Nula confirmation and then
 * deep-links back to the app, so the system browser lands on a real page
 * instead of being stranded on the provider's redirect spinner. `nula://`
 * is no longer a registered Auth0 redirect URI — it's only the deep-link
 * scheme the bounce page navigates to.
 *
 * Direct-to-social: when `connection=google-oauth2` (or `apple`) is set on
 * `/authorize`, Auth0 skips its own hosted login page and redirects directly
 * to the social provider. That's why users never see an Auth0-branded page.
 *
 * The Tauri commands validate the `token_url` host against the build-time
 * pinned Auth0 domain (audit finding #17) — see
 * apps/lumen/core/src/commands/auth.rs.
 */

// Env vars are read lazily (inside functions) rather than at module load so
// they're testable: vi.stubEnv() in tests takes effect on each access. The
// `as string` casts are intentional — missing values throw a clearer error
// at the call site via the `if (!auth0ClientId())` guards.
function auth0Domain(): string {
  return import.meta.env.VITE_AUTH0_DOMAIN as string;
}
function auth0ClientId(): string {
  return import.meta.env.VITE_AUTH0_CLIENT_ID as string;
}
function auth0Audience(): string {
  return import.meta.env.VITE_AUTH0_AUDIENCE as string;
}

function authorizeUrl(): string {
  return `https://${auth0Domain()}/authorize`;
}
function tokenUrl(): string {
  return `https://${auth0Domain()}/oauth/token`;
}
function revokeUrlEndpoint(): string {
  return `https://${auth0Domain()}/oauth/revoke`;
}
function userInfoUrl(): string {
  return `https://${auth0Domain()}/userinfo`;
}

/**
 * Resolve the OAuth `redirect_uri` for the current platform. Pure (all inputs
 * explicit) so it's unit-testable without env/platform mocking, and so the
 * authorize request and the token exchange can share one source of truth — the
 * two MUST be byte-identical or the exchange fails.
 *
 * - **Desktop production:** `${webOrigin}/auth/desktop` — the hosted bounce
 *   page that deep-links back into the app (see module header).
 * - **Desktop dev:** the localhost Vite callback, so the redirect lands in the
 *   dev server where devtools are available.
 * - **Web:** `${webOrigin}/auth/callback` — same-tab redirect, exchanged in-page.
 *
 * @param webOrigin Hosted web origin. Web passes `window.location.origin`;
 *   desktop passes the build-time `VITE_DESKTOP_WEB_ORIGIN` (its own runtime
 *   origin is `tauri://localhost`, which Auth0 can't redirect to).
 */
export function authRedirectUri(opts: {
  isDesktop: boolean;
  isDev: boolean;
  webOrigin: string;
}): string {
  const { isDesktop, isDev, webOrigin } = opts;
  if (isDesktop && !isDev) return `${webOrigin}/auth/desktop`;
  if (isDesktop) return 'http://localhost:5173/auth/callback';
  return `${webOrigin}/auth/callback`;
}

export type { OAuthTokens } from '@/lib/platform/generated/bindings.gen';

export interface OAuthUserInfo {
  sub: string;
  email: string;
  email_verified: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
}

export interface PendingAuth {
  codeVerifier: string;
  state: string;
}

/**
 * Thrown by refreshAccessToken. `status` carries the HTTP status when known
 * (web path reads resp.status). It is `undefined` when the status can't be
 * determined — notably the desktop path, where the Rust command collapses the
 * HTTP error into a string. Session-resume uses `status` to distinguish a
 * DEFINITIVE token rejection (400/401 → clear the token) from a TRANSIENT
 * failure (network / 5xx / unknown → keep the token so the next launch can
 * resume). An unknown status is treated as transient by design: never destroy a
 * possibly-valid session on an unprovable failure.
 */
export class AuthRefreshError extends Error {
  status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AuthRefreshError';
    this.status = status;
  }
}

/**
 * Build the URL the system browser opens to start sign-in. Returns the
 * authorize URL plus the PKCE/state values we'll need at the callback to
 * complete the exchange (caller is responsible for stashing them).
 *
 * @param connection Optional Auth0 connection name (e.g. `google-oauth2`,
 *   `apple`). When set, Auth0 skips its own hosted login page and goes
 *   straight to the social provider — that's the whole point of having a
 *   custom Login UI on our domain.
 */
export async function startAuth(
  redirectUri: string,
  connection?: string,
): Promise<{ url: string; pending: PendingAuth }> {
  if (!auth0ClientId()) {
    throw new Error('VITE_AUTH0_CLIENT_ID must be set at build time');
  }
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();

  const params = new URLSearchParams({
    client_id: auth0ClientId(),
    response_type: 'code',
    redirect_uri: redirectUri,
    scope: 'openid email profile offline_access',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    state,
    audience: auth0Audience(),
    ...(import.meta.env.DEV && { prompt: 'login' }),
  });
  if (connection) {
    params.set('connection', connection);
  }

  return {
    url: `${authorizeUrl()}?${params.toString()}`,
    pending: { codeVerifier, state },
  };
}

/**
 * Exchange the authorization code we got back at the redirect for an access
 * token + id_token + (optional) refresh_token. On Tauri builds this routes
 * through a Rust command to dodge CORS (Auth0's token endpoint doesn't allow
 * `tauri://localhost` as an Origin).
 */
export async function exchangeCodeForTokens(
  code: string,
  codeVerifier: string,
  redirectUri: string,
): Promise<OAuthTokens> {
  if (!auth0ClientId()) {
    throw new Error('VITE_AUTH0_CLIENT_ID must be set at build time');
  }
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: auth0ClientId(),
    code,
    code_verifier: codeVerifier,
    redirect_uri: redirectUri,
  });

  if (isDesktop) {
    const res = await commands.exchangeAuthCode(tokenUrl(), body.toString());
    if (res.status === 'error') throw new Error(`Token exchange failed: ${res.error}`);
    return res.data;
  }

  const resp = await fetch(tokenUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!resp.ok) {
    const err = await resp.text().catch(() => '');
    throw new Error(`Token exchange failed (${resp.status}): ${err}`);
  }
  return resp.json() as Promise<OAuthTokens>;
}

/**
 * Refresh-token grant. Exchanges a previously-stored refresh_token for a
 * fresh access_token + id_token (and a new refresh_token — Auth0's Refresh
 * Token Rotation is enabled on the SPA, so the old refresh token is
 * invalidated on every successful refresh).
 */
export async function refreshAccessToken(refreshToken: string): Promise<OAuthTokens> {
  if (!auth0ClientId()) {
    throw new Error('VITE_AUTH0_CLIENT_ID must be set at build time');
  }
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: auth0ClientId(),
    refresh_token: refreshToken,
  });

  if (isDesktop) {
    const res = await commands.refreshAuthToken(tokenUrl(), body.toString());
    // The Rust command collapses the HTTP status into a string, so we can't
    // recover it here — status stays undefined (treated as transient upstream).
    if (res.status === 'error') throw new AuthRefreshError(`Token refresh failed: ${res.error}`);
    return res.data;
  }

  const resp = await fetch(tokenUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!resp.ok) {
    const err = await resp.text().catch(() => '');
    throw new AuthRefreshError(`Token refresh failed (${resp.status}): ${err}`, resp.status);
  }
  return resp.json() as Promise<OAuthTokens>;
}

/**
 * Revoke a refresh token server-side. RFC 7009. Best-effort — if Auth0 is
 * unreachable we still clear local state. Callers should treat this as
 * fire-and-forget.
 */
export async function revokeToken(token: string): Promise<void> {
  if (!auth0ClientId()) return;
  const body = new URLSearchParams({
    client_id: auth0ClientId(),
    token,
  });
  try {
    if (isDesktop) {
      const res = await commands.revokeOauthToken(revokeUrlEndpoint(), body.toString());
      if (res.status === 'error') {
        // Non-fatal: token revoke failures don't block sign-out (local state still clears)
        console.warn('[auth0] token revoke failed (non-fatal)', res.error);
      }
      return;
    }
    await fetch(revokeUrlEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch (err) {
    console.warn('[auth0] token revoke failed (non-fatal)', err);
  }
}

/**
 * Fetch the OIDC userinfo claims with a valid access_token. Auth0 includes
 * `sub`, `email`, `name`, `picture` here — same shape as the inline custom
 * claims our Post Login Action injects into the access token. Kept as a
 * fallback for the case where claims are missing (Action mis-deployed, etc).
 */
export async function fetchUserInfo(accessToken: string): Promise<OAuthUserInfo> {
  const resp = await fetch(userInfoUrl(), {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!resp.ok) {
    throw new Error(`Userinfo fetch failed (${resp.status})`);
  }
  return resp.json() as Promise<OAuthUserInfo>;
}

// ── Passwordless email (OTP code) ───────────────────────────────────────────
// Two-step flow: /passwordless/start sends an email containing a 6-digit
// code, then /oauth/token (with the passwordless OTP grant_type) exchanges
// the code for tokens. Desktop routes the token exchange through the same
// `exchange_auth_code` Tauri command we use for the social flow — it
// validates the token_url host against the build-time pin.

const PASSWORDLESS_OTP_GRANT = 'http://auth0.com/oauth/grant-type/passwordless/otp';

function passwordlessStartUrl(): string {
  return `https://${auth0Domain()}/passwordless/start`;
}

/**
 * Kick off the passwordless email flow. Auth0 emails the user a 6-digit OTP.
 * Returns void on success; throws on network or 4xx with the Auth0 error
 * description in the message.
 *
 * Desktop routes through the `start_passwordless` Tauri command: WKWebView
 * rejects the response from `/passwordless/start` for the `tauri://localhost`
 * origin client-side, even though Auth0 sends a permissive
 * `Access-Control-Allow-Origin: *` header — the same CORS bypass the other
 * three Auth0 endpoints already need.
 */
export async function startEmailPasswordless(email: string): Promise<void> {
  if (!auth0ClientId()) {
    throw new Error('VITE_AUTH0_CLIENT_ID must be set at build time');
  }
  const body = JSON.stringify({
    client_id: auth0ClientId(),
    connection: 'email',
    email,
    send: 'code',
  });

  if (isDesktop) {
    const res = await commands.startPasswordless(passwordlessStartUrl(), body);
    if (res.status === 'error') throw new Error(`Could not send sign-in code: ${res.error}`);
    return;
  }

  const resp = await fetch(passwordlessStartUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
  });
  if (!resp.ok) {
    let detail = `${resp.status}`;
    try {
      const errBody = await resp.json();
      detail = errBody.error_description || errBody.error || detail;
    } catch {
      // body wasn't JSON — fall through with the status code
    }
    throw new Error(`Could not send sign-in code: ${detail}`);
  }
}

/**
 * Exchange a user-entered OTP code for tokens. Same /oauth/token endpoint
 * as the social flow, different grant_type. Desktop routes through the
 * Rust `exchange_auth_code` Tauri command to dodge CORS.
 */
export async function verifyEmailOtp(email: string, otp: string): Promise<OAuthTokens> {
  if (!auth0ClientId()) {
    throw new Error('VITE_AUTH0_CLIENT_ID must be set at build time');
  }
  const body = new URLSearchParams({
    grant_type: PASSWORDLESS_OTP_GRANT,
    client_id: auth0ClientId(),
    username: email,
    otp,
    realm: 'email',
    scope: 'openid email profile offline_access',
    audience: auth0Audience(),
  });

  if (isDesktop) {
    const res = await commands.exchangeAuthCode(tokenUrl(), body.toString());
    if (res.status === 'error') throw new Error(`OTP exchange failed: ${res.error}`);
    return res.data;
  }

  const resp = await fetch(tokenUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!resp.ok) {
    let detail = `${resp.status}`;
    try {
      const errBody = await resp.json();
      detail = errBody.error_description || errBody.error || detail;
    } catch {
      // body wasn't JSON
    }
    throw new Error(`OTP verification failed: ${detail}`);
  }
  return resp.json() as Promise<OAuthTokens>;
}

/** Parse `code` and `state` out of a redirect-back URL. */
export function parseCallback(url: string): {
  code: string | null;
  state: string | null;
  error: string | null;
} {
  try {
    const u = new URL(url);
    return {
      code: u.searchParams.get('code'),
      state: u.searchParams.get('state'),
      error: u.searchParams.get('error'),
    };
  } catch {
    return { code: null, state: null, error: 'invalid_url' };
  }
}
